import React, { createContext, useState, useEffect, useContext } from "react";
import { auth } from "./firebase";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { authAPI } from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get Firebase ID token
          const token = await firebaseUser.getIdToken();
          
          // Get user data from backend using the token
          const response = await fetch('https://vastudesignconsultants-735ed.web.app/api/auth/verify', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const userData = await response.json();
            const userInfo = {
              ...userData.user,
              isAuthenticated: true,
              isAdmin: userData.user.role === 'admin',
              firebaseUser: firebaseUser
            };
            
            // Store user data in localStorage for API access
            localStorage.setItem("userData", JSON.stringify(userInfo));
            setUser(userInfo);
          } else {
            console.error('Failed to verify user with backend');
            setUser(null);
            localStorage.removeItem("userData");
          }
        } catch (error) {
          console.error('Error getting user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
        localStorage.removeItem("userData");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (credentials) => {
    try {
      // First, get user data from backend to get the email
      const response = await authAPI.login(credentials);
      
      if (!response.user) {
        return { success: false, error: "Invalid credentials" };
      }

      // Sign in with Firebase using email and password
      await signInWithEmailAndPassword(
        auth, 
        response.user.email, 
        credentials.password
      );

      // Firebase auth state change will handle setting the user
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || "Login failed" 
      };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      localStorage.removeItem("userData");
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
